import api from './api';

export interface ActiveSession {
  userId: string;
  username: string;
  name: string;
  connectedAt: string;
  lastActivity: string;
  isOnline: boolean;
}

export interface SessionsResponse {
  [userId: string]: ActiveSession;
}

class SessionService {
  private heartbeatInterval: NodeJS.Timeout | null = null;

  // Obtenir toutes les sessions actives
  async getActiveSessions(): Promise<SessionsResponse> {
    try {
      const response = await api.get('/sessions/active');
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération des sessions actives:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la récupération des sessions actives');
    }
  }

  // Obtenir le nombre d'utilisateurs connectés
  async getOnlineUsersCount(): Promise<number> {
    try {
      const response = await api.get('/sessions/count');
      return response.data.count;
    } catch (error: any) {
      console.error('Erreur lors du comptage des utilisateurs connectés:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors du comptage des utilisateurs connectés');
    }
  }

  // Envoyer un heartbeat pour maintenir la session active
  async sendHeartbeat(): Promise<void> {
    try {
      await api.post('/sessions/heartbeat');
    } catch (error: any) {
      console.error('Erreur lors de l\'envoi du heartbeat:', error);
      // Ne pas lever d'erreur pour le heartbeat pour éviter de perturber l'UX
    }
  }

  // Démarrer le heartbeat automatique (toutes les 30 secondes pour plus de réactivité)
  startHeartbeat(): void {
    if (this.heartbeatInterval) {
      this.stopHeartbeat();
    }

    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, 30 * 1000); // Toutes les 30 secondes (plus fréquent)

    // Envoyer un heartbeat immédiatement
    this.sendHeartbeat();
  }

  // Arrêter le heartbeat automatique
  stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // Formater la durée de connexion
  formatConnectionDuration(connectedAt: string): string {
    const now = new Date();
    const connected = new Date(connectedAt);
    const diffMs = now.getTime() - connected.getTime();
    
    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}j ${hours % 24}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else {
      return `${minutes}m`;
    }
  }

  // Formater la dernière activité
  formatLastActivity(lastActivity: string): string {
    const now = new Date();
    const activity = new Date(lastActivity);
    const diffMs = now.getTime() - activity.getTime();
    
    const minutes = Math.floor(diffMs / (1000 * 60));
    
    if (minutes < 1) {
      return 'À l\'instant';
    } else if (minutes < 60) {
      return `Il y a ${minutes}m`;
    } else {
      const hours = Math.floor(minutes / 60);
      return `Il y a ${hours}h`;
    }
  }
}

const sessionService = new SessionService();
export default sessionService; 